import { Component, signal, computed, ChangeDetectionStrategy, OnInit, inject, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ThemeToggleComponent } from '../components/theme-toggle/theme-toggle.component';
import { LanguageSelectorComponent } from '../components/language-selector/language-selector.component';
import { CurrentIdentityService } from '../services/current-identity.service';

// PrimeNG imports
import { Toast } from 'primeng/toast';
import { MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { UserRole } from '@libs/core/api';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    Toast,
    ThemeToggleComponent,
    LanguageSelectorComponent,
    AvatarModule,
    ButtonModule,
    MenuModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.css'],
})
export class LayoutComponent implements OnInit {
  private identityService = inject(CurrentIdentityService);
  private translate = inject(TranslateService);

  sidebarVisible = signal(true);
  currentUser = this.identityService.currentUser;

  // Signal to track current language for reactivity
  currentLang = signal(this.translate.currentLang || 'it');

  // Dynamic menu items based on user role
  menuItems = computed(() => {
    // Make this computed reactive to language changes
    this.currentLang();
    const items: MenuItem[] = [];

    // Dashboard - All roles
    if (this.identityService.canViewDashboard()) {
      items.push({
        label: this.translate.instant('nav.dashboard'),
        icon: 'pi pi-home',
        routerLink: '/dashboard',
      });
    }

    // Reports - Admin, TL, CodeQualityResponsible
    if (this.identityService.canViewReports()) {
      items.push({
        label: this.translate.instant('nav.weeklyReport'),
        icon: 'pi pi-calendar-plus',
        routerLink: '/weekly-report',
      });
      items.push({
        label: this.translate.instant('nav.monthlyReport'),
        icon: 'pi pi-calendar',
        routerLink: '/monthly-report',
      });
      items.push({
        label: this.translate.instant('nav.codeQualityReport'),
        icon: 'pi pi-check',
        routerLink: '/code-quality-report',
      });
    }

    // Projects - Admin, TL
    if (this.identityService.canViewProjects()) {
      items.push({
        label: this.translate.instant('nav.projects'),
        icon: 'pi pi-chart-bar',
        routerLink: '/projects',
      });
    }

    // Quality Gates - Admin, TL, CodeQualityResponsible
    if (this.identityService.canViewQualityGates()) {
      items.push({
        label: this.translate.instant('nav.qualityGates'),
        icon: 'pi pi-shield',
        routerLink: '/quality-gates',
      });
    }

    // Users - Admin, TL
    if (this.identityService.canViewUsers()) {
      items.push({
        label: this.translate.instant('nav.users'),
        icon: 'pi pi-users',
        routerLink: '/users',
      });
    }

    // Teams - Admin only
    if (this.identityService.canViewTeams()) {
      items.push({
        label: this.translate.instant('nav.teams'),
        icon: 'pi pi-sitemap',
        routerLink: '/teams',
      });
    }

    // Team Settings - TL only
    if (this.identityService.canViewTeamSettings()) {
      items.push({
        label: this.translate.instant('nav.teamSettings'),
        icon: 'pi pi-cog',
        routerLink: '/team-settings',
      });
    }

    return items;
  });

  userMenuItems = computed<MenuItem[]>(() => {
    // Make this computed reactive to language changes
    this.currentLang();
    return [
      {
        label: this.translate.instant('nav.logout'),
        icon: 'pi pi-sign-out',
        command: () => this.logout(),
      },
    ];
  });

  ngOnInit() {
    // Load current user
    this.identityService.loadCurrentUser();

    // Subscribe to language changes to update signal
    this.translate.onLangChange.subscribe((event) => {
      this.currentLang.set(event.lang);
    });
  }

  toggleSidebar() {
    this.sidebarVisible.update((visible) => !visible);
  }

  getUserInitials(): string {
    const user = this.currentUser();
    if (!user) return '?';

    const names = user.fullName.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return user.fullName.substring(0, 2).toUpperCase();
  }

  // Computed for role labels that updates when language changes
  getRoleLabel = computed(() => {
    // Make this reactive to language changes
    this.currentLang();
    const user = this.currentUser();
    if (!user) return '';

    const roleLabels: Record<string, string> = {
      [UserRole.Admin]: this.translate.instant('roles.admin'),
      [UserRole.TechnicalLeader]: this.translate.instant('roles.technicalLeader'),
      [UserRole.CodeQualityResponsible]: this.translate.instant('roles.codeQualityResponsible'),
      [UserRole.Member]: this.translate.instant('roles.member'),
    };
    return roleLabels[user.role] || user.role;
  });

  async logout() {
    try {
      await this.identityService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }
}
